/**
 * visitHistoryApi.ts
 *
 * 通話終了後の来訪履歴登録APIコール処理。
 * 指示書「通話終了後の来訪履歴登録API呼び出しフロー」に基づいて実装。
 */

import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";

type VisitHistoryCaller = (params: PostVisitHistoryParams) => Promise<void>;

// ----------------------
// 型定義
// ----------------------

/** 通話画面に遷移する際に渡されるボタン種別 */
export type MenuButtonType =
  | "qr"
  | "receptionCode"
  | "group"
  | "user"
  | "recruit"
  | "other"
  | "secretary"
  | "delivery"
  | "taxi"
  | "faceRecognition";

/** チャットリクエスト種別 */
export type ChatRequestType = "success" | "failed" | "noRequest";

/** 来訪履歴登録に必要なパラメータ */
export interface PostVisitHistoryParams {
  isCalling: boolean;
  chatRequestType: ChatRequestType;
  isAppointmentHistoryEnabled: boolean;
  menuButtonType: MenuButtonType;
  /** appointmentId（qr/receptionCode のみ使用） */
  appointmentId?: number | null;
  /** 来訪者IDリスト（qr/receptionCode のみ使用） */
  visitorIds?: number[] | null;
  buttonTitle?: string | null;
  notificationType?: string | null;
  groupName?: string | null;
  userName?: string | null;
  companyName?: string | null;
  visitorName?: string | null;
  requirement?: string | null;
}

// ----------------------
// エンドポイント
// ----------------------

const visitHistoryGeneralUrl = buildIfaceApiUrl("v1/receptions/visit-history/general");
const visitHistoryAppointmentUrl = buildIfaceApiUrl("v1/receptions/visit-history/appointment");

// ----------------------
// 内部ヘルパー：API呼び出し
// ----------------------

const postAppointmentVisitHistory = async (
  appointmentId: number,
  appointmentVisitorIds: number[],
  route: string
): Promise<void> => {
  await apiClient.post(
    visitHistoryAppointmentUrl,
    { appointmentId: String(appointmentId), appointmentVisitorIds, route },
    { headers: createIfaceHeaders(true) }
  );
};

const postGeneralVisitHistory = async (body: Record<string, unknown>): Promise<void> => {
  await apiClient.post(visitHistoryGeneralUrl, body, { headers: createIfaceHeaders(true) });
};

// ----------------------
// menuButtonType 別 API呼び出しロジック
// ----------------------

const callQrOrReceptionCode = async (params: PostVisitHistoryParams): Promise<void> => {
  const { appointmentId, visitorIds, menuButtonType } = params;
  if (appointmentId === null || appointmentId === undefined || !visitorIds?.length) return;
  await postAppointmentVisitHistory(appointmentId, visitorIds, menuButtonType);
};

const callGroup = async (params: PostVisitHistoryParams): Promise<void> => {
  const { buttonTitle, notificationType, groupName, companyName, visitorName, requirement } =
    params;
  if (!buttonTitle || !notificationType || !groupName) return;
  await postGeneralVisitHistory({
    route: "group",
    affiliation: companyName ?? null,
    visitorName: visitorName ?? null,
    business: requirement ?? null,
    groupName,
    userName: null,
    buttonName: buttonTitle,
    notificationType,
  });
};

const callUser = async (params: PostVisitHistoryParams): Promise<void> => {
  const {
    buttonTitle,
    notificationType,
    groupName,
    userName,
    companyName,
    visitorName,
    requirement,
  } = params;
  if (!buttonTitle || !notificationType || !groupName || !userName) return;
  await postGeneralVisitHistory({
    route: "user",
    affiliation: companyName ?? null,
    visitorName: visitorName ?? null,
    business: requirement ?? null,
    groupName,
    userName,
    buttonName: buttonTitle,
    notificationType,
  });
};

const callRecruitOtherSecretary = async (params: PostVisitHistoryParams): Promise<void> => {
  const { buttonTitle, notificationType, menuButtonType, companyName, visitorName, requirement } =
    params;
  if (!buttonTitle || !notificationType) return;
  await postGeneralVisitHistory({
    route: menuButtonType,
    affiliation: companyName ?? null,
    visitorName: visitorName ?? null,
    business: requirement ?? null,
    groupName: null,
    userName: null,
    buttonName: buttonTitle,
    notificationType,
  });
};

const callDelivery = async (params: PostVisitHistoryParams): Promise<void> => {
  const { buttonTitle, notificationType, companyName } = params;
  if (!buttonTitle || !notificationType) return;
  await postGeneralVisitHistory({
    route: "delivery",
    affiliation: companyName ?? null,
    visitorName: null,
    business: null,
    groupName: null,
    userName: null,
    buttonName: buttonTitle,
    notificationType,
  });
};

const skipVisitHistory = ({
  isCalling,
  chatRequestType,
  isAppointmentHistoryEnabled,
}: PostVisitHistoryParams): boolean => {
  if (!isCalling) return true;
  if (chatRequestType === "success") return true;
  if (!isAppointmentHistoryEnabled) return true;
  return false;
};

const noopVisitHistory: VisitHistoryCaller = async () => undefined;

const VISIT_HISTORY_CALLERS: Record<MenuButtonType, VisitHistoryCaller> = {
  qr: callQrOrReceptionCode,
  receptionCode: callQrOrReceptionCode,
  group: callGroup,
  user: callUser,
  recruit: callRecruitOtherSecretary,
  other: callRecruitOtherSecretary,
  secretary: callRecruitOtherSecretary,
  delivery: callDelivery,
  taxi: noopVisitHistory,
  faceRecognition: noopVisitHistory,
};

// ----------------------
// 公開関数
// ----------------------

/**
 * 通話終了後の来訪履歴登録APIを呼び出す。
 *
 * 指示書のフロー定義に従い、条件を満たす場合のみAPIをコールする。
 * 成功・失敗に関わらず例外をスローしない（呼び出し元で await するだけでよい）。
 */
export const postVisitHistory = async (params: PostVisitHistoryParams): Promise<void> => {
  if (skipVisitHistory(params)) return;

  try {
    await VISIT_HISTORY_CALLERS[params.menuButtonType](params);
  } catch (err) {
    // 成功・失敗に関わらず後続処理は同一のため、エラーは無視する
    console.error("Failed to post visit history:", err);
  }
};
